import type { ReactNode } from "react";

const SUCCESS_TITLE_CLASS = "text-[#003832] text-base font-semibold";
const SUCCESS_DESCRIPTION_CLASS = "text-[#2B4752] text-[14px]";
const ERROR_TITLE_CLASS = "text-[#73134A] text-base font-semibold";
const ERROR_DESCRIPTION_CLASS = "text-[#260618] text-[14px]";

type NotificationConfig = {
  title: ReactNode;
  description: ReactNode;
  placement: "bottom";
  className: "insight-success-confirmation" | "insight-error-confirmation";
  duration: number;
  icon: null;
};

function buildSuccessNotification(
  title: string,
  description: string,
): NotificationConfig {
  return {
    title: <p className={SUCCESS_TITLE_CLASS}>{title}</p>,
    description: <p className={SUCCESS_DESCRIPTION_CLASS}>{description}</p>,
    placement: "bottom",
    className: "insight-success-confirmation",
    duration: 3,
    icon: null,
  };
}

function buildErrorNotification(
  title: string,
  description: string,
): NotificationConfig {
  return {
    title: <p className={ERROR_TITLE_CLASS}>{title}</p>,
    description: <p className={ERROR_DESCRIPTION_CLASS}>{description}</p>,
    placement: "bottom",
    className: "insight-error-confirmation",
    duration: 4,
    icon: null,
  };
}

export function buildInviteSuccessNotification(inviteCount: number) {
  return buildSuccessNotification(
    inviteCount === 1 ? "User invited" : "Users invited",
    inviteCount === 1
      ? "The user was invited successfully."
      : "The selected users were invited successfully.",
  );
}

export function buildInviteErrorNotification(description: string) {
  return buildErrorNotification("Invite failed", description);
}

export function buildInvitePartialErrorNotification(
  failedCount: number,
  totalCount: number,
) {
  return buildErrorNotification(
    "Some invites failed",
    `${failedCount} of ${totalCount} invites failed. Retry will resend only the failed invites.`,
  );
}

export function buildUserSettingsUpdatedNotification() {
  return buildSuccessNotification(
    "User settings updated",
    "User settings were updated successfully.",
  );
}

export function buildUpdateFailedNotification(description: string) {
  return buildErrorNotification("Update failed", description);
}

export function buildUserDeactivatedNotification(userName: string) {
  return buildSuccessNotification(
    "User deactivated",
    `${userName} was deactivated successfully.`,
  );
}

export function buildDeactivateFailedNotification(
  userName: string,
  description: string,
) {
  return buildErrorNotification(
    "Deactivation failed",
    description ||
      `There was an error deactivating ${userName}. Please try again.`,
  );
}
